import { Deferred, deferred } from './deferred.js';

/**
 *
 * Taken from https://www.npmjs.com/package/@yornaath/batshit
 *
 * MIT License
 *
 * Copyright (c) 2022-present Jørn Andre
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 *
 */

/**
 * Batcher.
 * A batch manager that will batch requests for a certain data type within a given window.
 *
 * @generic T - The type of the data.
 * @generic Q - item query type
 * @generic C - the context of the batcher passed to the fetcher function
 */
export type Batcher<T, Q, C, R = T> = {
  /**
   * Schedule a get request for a query.
   *
   * @generic T - The type of the data.
   * @generic Q - item query type
   * @param query Q
   * @returns Promise<T>
   */
  fetch: (query: Q, context?: C) => Promise<R>;
};

/**
 * Config needed to create a Batcher
 *
 * @generic T - The type of the data.
 * @generic Q - item query type
 * @generic C - the context of the batcher passed to the fetcher function
 */
export type BatcherConfig<T, Q, C, R> = {
  /**
   * The function that makes the batched request for the current batch queries
   *
   * @param queries Q[]
   * @returns Promise<T
   */
  fetcher: (queries: Q[], context?: C) => Promise<T[]>;
  /**
   * The scheduling function.
   */
  scheduler?: BatcherScheduler;
  /**
   * Correlate an item by its query. Used to extract the correct value from the batch of items
   * to the correct query used to fetch it.
   *
   * @param query Q
   * @returns string
   */
  resolver: (items: T[], query: Q) => R;
  /**
   * Display name of the batcher. Used for debugging and devtools.
   */
  name?: string;
};

/**
 * A function to schedule batch execution timing
 */
export type BatcherScheduler = {
  /**
   * A scheduler function.
   *
   * @param start number - time stamp when the current batch started queuing fetches.
   * @param latest number - time stamp of the latest queued fetch.
   * @returns number - the number of ms to wait from latest queued fetch until executing batchh fetch call.
   */
  (start: number, latest: number, batchSize: number): Schedule;
};

/**
 * A schedule for when to execute a batched fetch call.
 */
export type Schedule = number | 'immediate' | 'never';

export type BatcherMemory<T, Q> = {
  seq: number;
  batch: Set<Q>;
  currentRequest: Deferred<T>;
  timer?: NodeJS.Timeout | undefined;
  start?: number | null;
  latest?: number | null;
};

/**
 * Create a batch manager for a given collection of a data type.
 * Will batch all .get calls given inside a scheduled time window into a singel request.
 *
 * @generic T - The type of the data.
 * @generic Q - item query type
 * @generic C - the context of the batcher passed to the fetcher function
 * @param config BatcherConfig<T, Q>
 * @returns Batcher<T, Q>
 */
export const create = <T, Q, C, R = T>(
  config: BatcherConfig<T, Q, C, R>,
  memory?: BatcherMemory<T[], Q>,
): Batcher<T, Q, C, ReturnType<(typeof config)['resolver']>> => {
  const scheduler: BatcherScheduler = config.scheduler ?? windowScheduler(10);

  const mem: BatcherMemory<T[], Q> = memory ?? {
    seq: 0,
    batch: new Set<Q>(),
    currentRequest: deferred<T[]>(),
    timer: undefined,
    start: null,
    latest: null,
  };

  const nextBatch = () => {
    mem.batch = new Set();
    mem.currentRequest = deferred<T[]>();
    mem.timer = undefined;
    mem.start = null;
    mem.latest = null;
  };

  const fetch = (query: Q, context?: C): Promise<R> => {
    if (!mem.start) mem.start = Date.now();
    mem.latest = Date.now();

    mem.batch.add(query);
    clearTimeout(mem.timer);

    const scheduled = scheduler(mem.start, mem.latest, mem.batch.size);

    const fetchBatch = () => {
      const req = config.fetcher([...mem.batch], context);
      const currentRequest = mem.currentRequest;

      nextBatch();

      req
        .then((data) => {
          currentRequest.resolve(data);
        })
        .catch((error) => {
          currentRequest.reject(error);
        });

      mem.seq++;

      return req;
    };

    if (scheduled === 'immediate') {
      const req = mem.currentRequest;
      fetchBatch();
      return req.value.then((items) => config.resolver(items, query));
    } else if (scheduled === 'never') {
      return mem.currentRequest.value.then((items) => config.resolver(items, query));
    } else {
      mem.timer = setTimeout(fetchBatch, scheduled);
      return mem.currentRequest.value.then((items) => config.resolver(items, query));
    }
  };

  return { fetch };
};

/**
 * Resolve by item field of items when response is an array.
 * Create a euquality check to check if the query matches a given key on the item data.
 *
 * @param key keyof T
 * @returns (item:T extends Array<A>, query: Q) => A
 */
export const keyResolver =
  <T extends Array<any>, Q, R = T extends Array<infer A> ? A : never>(
    key: T extends Array<infer A> ? keyof A : never,
  ) =>
  (items: T, query: Q): R =>
    items.find((item) => item[key] == query);

/**
 * Resolve by record index when response is an object.
 * Create a euquality check to check if the query matches a given key on the item data.
 *
 * @returns (item:T extends Record<_, A>, query: Q) => A
 */
export const indexedResolver =
  <T extends Record<any, any>, Q>() =>
  (itemsIndex: T, query: Q) =>
    itemsIndex[query];

/**
 * Give a window in ms where all queued fetched made within the window will be batched into
 * one singler batch fetch call.
 *
 * @param ms number
 * @returns BatcherScheduler
 */
export const windowScheduler: (ms: number) => BatcherScheduler = (ms) => (start, latest) => {
  const spent = latest - start;
  return ms - spent;
};

/**
 * Give a buffer time in ms. Will give another buffer window when queueing a fetch.
 *
 * @param ms number
 * @returns BatcherScheduler
 */
export const bufferScheduler: (ms: number) => BatcherScheduler = (ms) => () => {
  return ms;
};

/**
 * Same as windowScheduler, will batch calls made within a window of time OR when the max batch size is reached.
 *
 * @param config: {windowMs: number; maxBatchSize: number;}
 * @returns BatcherScheduler
 */
export const windowedFiniteBatchScheduler: (config: {
  windowMs: number;
  maxBatchSize: number;
}) => BatcherScheduler =
  ({ windowMs, maxBatchSize }) =>
  (start, latest, batchSize) => {
    if (batchSize >= maxBatchSize) return 'immediate';
    const spent = latest - start;
    return windowMs - spent;
  };

/**
 * Will batch calls when the max batch size is reached.
 *
 * @param config: {maxBatchSize: number;}
 * @returns BatcherScheduler
 */
export const maxBatchSizeScheduler: (config: { maxBatchSize: number }) => BatcherScheduler =
  ({ maxBatchSize }) =>
  (_start, _latest, batchSize) => {
    if (batchSize >= maxBatchSize) return 'immediate';
    return 'never';
  };
